import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useMemo } from "react";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import WorkAgreementLinks from "work/proposals/_views/_shared/work-agreement-links";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import DateRedlinePicker from "work/proposals/redlining/field-redlines/_views/date-redline-picker";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline, { ProposalRedlineUpdate } from "work/proposals/redlining/proposal-redline";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import { ReviewerEditLockAdornment } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import {
  proposalDetailsColumnGap,
  proposalDetailsControlMinimumWidth,
  proposalDetailsGridTemplateColumns,
} from "work/proposals/_views/_shared/proposal-details-layout";
import {
  paneIsCompact,
  workPaneProseInset,
  workPaneStretchedProseInset,
} from "work/_views/work-pane-layout";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalContainer = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    gridTemplateColumns: "1fr",
    rowGap: theme.spacing(1),
    // One stretched column, so every field reaches the row's far end and needs the inset there too
    // — see `workPaneStretchedProseInset`.
    ...workPaneStretchedProseInset(theme),
  },
  // This tab is all fields and no table, so the whole of it is prose — see `workPaneProseInset`.
  // Flush, the first field's border box touched the navigation drawer and its focus ring had
  // nowhere to go.
  ...workPaneProseInset(theme),
  alignItems: "start",
  display: "grid",
  columnGap: proposalDetailsColumnGap,
  rowGap: theme.spacing(2),
  gridTemplateColumns: proposalDetailsGridTemplateColumns(theme.spacing(50)),
  justifyContent: "start",
  width: "100%",
}));
const StatusSection = styled("div")(({ theme }) => ({
  alignItems: "flex-end",
  alignSelf: "stretch",
  display: "flex",
  gridColumn: "1 / -1",
  paddingTop: theme.spacing(2),
}));
const GeneralSection = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    rowGap: 0,
  },
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));
const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    marginBottom: theme.spacing(1),
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  width: "min-content",
}));
const NameTextField = styled(TextRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ theme, active }) => ({
  [paneIsCompact(theme)]: {
    margin: theme.spacing(1, 0),
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const DescriptionTextField = styled(TextRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ theme, active }) => ({
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const HighlightRedlineDatePicker = styled(DateRedlinePicker, {
  shouldForwardProp: (prop) => prop !== "active",
})<{
  active?: boolean;
}>(({ theme, active }) => ({
  [paneIsCompact(theme)]: {
    margin: 0,
  },
  "&&": {
    margin: theme.spacing(2, 0),
    [paneIsCompact(theme)]: {
      margin: 0,
    },
  },
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));

type DetailsTabProps = {
  /** The proposal on the page, for the History field's links to the work it revises or produced. */
  proposal: Proposal;
  proposalStatus: string;
  activeReviewField: ProposalField | undefined;
  proposalRedline: ProposalRedline;
  persistedRedline?: ProposalRedline;
  isReviewing?: boolean;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /**
   * Why the viewer cannot restore an earlier value, stated in each field's read-only history while
   * editing is disabled. See `proposalFieldRestoreLockReason`.
   */
  historyReadOnlyReason?: string;
  negotiationId?: string;
  viewerScope?: string;
  clientLeaderUserId: Guid | null;
  clientRepresentativeName?: string | null;
  onProposalRedlineChange: (newRedline: ProposalRedlineUpdate, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
};

const DetailsTab = (props: Readonly<DetailsTabProps>) => {
  const {
    proposal,
    proposalStatus,
    proposalRedline,
    persistedRedline,
    isReviewing,
    activeReviewField,
    disableEditing,
    fieldLockTooltip,
    historyReadOnlyReason,
    negotiationId,
    viewerScope,
    clientLeaderUserId,
    clientRepresentativeName,
    onProposalRedlineChange,
    onTraverseToNewField,
  } = props;

  // Every session that reaches this tab may resolve a user id it was already given: the by-id
  // lookups stand behind MarketplaceDirectoryAccess, which the restricted onboarding tranche grants
  // (issue #4830). There is no longer a viewer for whom this lookup has to be switched off.
  const clientLeaderIndividualQuery = useGetIndividualsByUserIdsQuery(
    clientLeaderUserId && !clientRepresentativeName
      ? { userIds: [clientLeaderUserId.value] }
      : skipToken,
  );

  const clientName = useMemo(() => {
    if (clientLeaderUserId === null) return "Masked";
    if (clientRepresentativeName) return clientRepresentativeName;
    const clientLeader = Individual.fromJSON(clientLeaderIndividualQuery.data?.[0]);
    if (clientLeader) return clientLeader.fullName;
    if (clientLeaderIndividualQuery.isLoading) return "Loading...";
    if (clientLeaderIndividualQuery.isError) return "Error loading client";
    return "Unknown client";
  }, [
    clientLeaderIndividualQuery.data,
    clientLeaderIndividualQuery.isError,
    clientLeaderIndividualQuery.isLoading,
    clientLeaderUserId,
    clientRepresentativeName,
  ]);
  const lockAdornment =
    disableEditing && fieldLockTooltip ? <ReviewerEditLockAdornment title={fieldLockTooltip} /> : undefined;
  const lockedFieldSx = lockAdornment
    ? {
        "& .MuiInputLabel-root.MuiInputLabel-shrink": {
          color: "warning.main",
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
          color: "warning.main",
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
          color: "warning.main",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
      }
    : undefined;

  return (
    proposalRedline && (
      <ProposalContainer>
        <StatusSection>
          <Typography variant="h6" sx={{ fontWeight: "normal", m: 0 }}>
            <strong>Status</strong>: {proposalStatus}
          </Typography>
        </StatusSection>
        <WorkAgreementLinks proposal={proposal} />
        <GeneralSection>
          {clientName && (
            <ClientTextField
              label="Client Representative"
              value={clientName}
              disabled
              sx={lockedFieldSx}
              slotProps={{
                input: {
                  startAdornment: lockAdornment,
                },
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          <NameTextField
            focused={activeReviewField?.name === ProposalFieldName.Name}
            required={!disableEditing}
            active={activeReviewField?.isEqualTo(ProposalField.Name)}
            className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.Name))}
            helperText={disableEditing ? "" : "required"}
            disabled={disableEditing}
            readOnly={disableEditing}
            lockAdornment={lockAdornment}
            textRedline={proposalRedline.name}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            // The same revert menu the draft page's Details fields have, while the field is editable.
            revertsFromHistory
            historyReadOnlyReason={historyReadOnlyReason}
            hideUndoButton={Boolean(isReviewing && persistedRedline?.name.isEqualTo(proposalRedline.name))}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.Name))}
            fieldConstructor={ProjectName}
            onTextFieldRedlineChange={(
              updatedRedline: FieldRedline<ProjectName>,
              traversalFieldOverride?: ProposalField | null,
            ) => {
              try {
                onProposalRedlineChange(
                  (currentRedline) => currentRedline.updateNameRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                showErrorSnackbar(error, { doing: "updating the proposal name", operation: "proposal.details.update-name" });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.Name);
            }}
          />

          <DescriptionTextField
            focused={activeReviewField?.name === ProposalFieldName.Description}
            active={activeReviewField?.isEqualTo(ProposalField.Description)}
            className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.Description))}
            required={!disableEditing}
            multiline={true}
            helperText={disableEditing ? "" : "required"}
            disabled={disableEditing}
            readOnly={disableEditing}
            lockAdornment={lockAdornment}
            minRows={3}
            textRedline={proposalRedline.description}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            // The same revert menu the draft page's Details fields have, while the field is editable.
            revertsFromHistory
            historyReadOnlyReason={historyReadOnlyReason}
            hideUndoButton={Boolean(
              isReviewing && persistedRedline?.description.isEqualTo(proposalRedline.description),
            )}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.Description))}
            fieldConstructor={ProjectDescription}
            onTextFieldRedlineChange={(
              updatedRedline: FieldRedline<ProjectDescription>,
              traversalFieldOverride?: ProposalField | null,
            ) => {
              try {
                onProposalRedlineChange(
                  (currentRedline) => currentRedline.updateDescriptionRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                showErrorSnackbar(error, { doing: "updating the proposal description", operation: "proposal.details.update-description" });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.Description);
            }}
          />
        </GeneralSection>
        <DateSection>
          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.ResponseDueBy}
            active={activeReviewField?.isEqualTo(ProposalField.ResponseDueBy)}
            className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.ResponseDueBy))}
            disabled={disableEditing}
            readOnly={disableEditing}
            lockAdornment={lockAdornment}
            dateRedline={proposalRedline.responseDueBy}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            // The same revert menu the draft page's Details fields have, while the field is editable.
            revertsFromHistory
            historyReadOnlyReason={historyReadOnlyReason}
            hideUndoButton={Boolean(
              isReviewing && persistedRedline?.responseDueBy.isEqualTo(proposalRedline.responseDueBy),
            )}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy))}
            minDate={new AHDate().asMoment}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(
                  (currentRedline) => currentRedline.updateResponseDueByRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                showErrorSnackbar(error, { doing: "updating the response due date", operation: "proposal.details.update-response-due-by" });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.ResponseDueBy);
            }}
          />

          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.StartDate}
            active={activeReviewField?.isEqualTo(ProposalField.StartDate)}
            className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.StartDate))}
            disabled={disableEditing}
            lockAdornment={lockAdornment}
            dateRedline={proposalRedline.startDate}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            // The same revert menu the draft page's Details fields have, while the field is editable.
            revertsFromHistory
            historyReadOnlyReason={historyReadOnlyReason}
            hideUndoButton={Boolean(isReviewing && persistedRedline?.startDate.isEqualTo(proposalRedline.startDate))}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.StartDate))}
            minDate={proposalRedline.responseDueBy.currentEntry?.asMoment}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(
                  (currentRedline) => currentRedline.updateStartDateRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                showErrorSnackbar(error, { doing: "updating the start date", operation: "proposal.details.update-start-date" });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.StartDate);
            }}
          />

          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.EndDate}
            active={activeReviewField?.isEqualTo(ProposalField.EndDate)}
            className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.EndDate))}
            disabled={disableEditing}
            lockAdornment={lockAdornment}
            dateRedline={proposalRedline.endDate}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            // The same revert menu the draft page's Details fields have, while the field is editable.
            revertsFromHistory
            historyReadOnlyReason={historyReadOnlyReason}
            hideUndoButton={Boolean(isReviewing && persistedRedline?.endDate.isEqualTo(proposalRedline.endDate))}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.EndDate))}
            minDate={proposalRedline.startDate.currentEntry?.asMoment}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(
                  (currentRedline) => currentRedline.updateEndDateRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                showErrorSnackbar(error, { doing: "updating the end date", operation: "proposal.details.update-end-date" });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.EndDate);
            }}
          />
        </DateSection>
      </ProposalContainer>
    )
  );
};

export default DetailsTab;
